import { JSX, PropsWithoutRef, ReactNode } from "react";
import CountUp from "react-countup";
import SectionTag from "~/components/SectionTag";
import useDailyStatistics from "~/hooks/api/useDailyStatistics";

export type StatisticsProps = Omit<PropsWithoutRef<JSX.IntrinsicElements["div"]>, "children">;

type Stat = {
  icon?: ReactNode;
  numberKey: keyof Statistics;
  text: string;
  variant: "info" | "primary" | "success";
};

const stats: Array<Stat> = [
  {
    icon: (
      <svg className="size-2_5rem text-success" fill="none" viewBox="0 0 39 39" xmlns="http://www.w3.org/2000/svg">
        <path
          d="M16.5533 28.8714C19.9748 28.8714 22.8831 27.6739 25.2782 25.2788C27.6733 22.8838 28.8708 19.9755 28.8708 16.554C28.8708 13.1325 27.6733 10.2243 25.2782 7.82922C22.8831 5.43418 19.9748 4.23665 16.5533 4.23665C13.1317 4.23665 10.2234 5.43418 7.82835 7.82922C5.43327 10.2243 4.23573 13.1325 4.23573 16.554C4.23573 19.9755 5.43327 22.8838 7.82835 25.2788C10.2234 27.6739 13.1317 28.8714 16.5533 28.8714ZM14.5003 22.7127V8.34245H18.6062V22.7127H14.5003ZM7.31512 22.7127V12.4482H11.421V22.7127H7.31512ZM21.6856 22.7127V14.5011H25.7914V22.7127H21.6856ZM35.2538 38.1289L26.6639 29.5386C25.2611 30.6335 23.7043 31.4803 21.9935 32.0791C20.2828 32.6778 18.4693 32.9772 16.5533 32.9772C11.9684 32.9772 8.08496 31.3862 4.90293 28.2042C1.7209 25.0222 0.129883 21.1388 0.129883 16.554C0.129883 11.9692 1.7209 8.08584 4.90293 4.90384C8.08496 1.72185 11.9684 0.130859 16.5533 0.130859C21.1381 0.130859 25.0216 1.72185 28.2036 4.90384C31.3856 8.08584 32.9767 11.9692 32.9767 16.554C32.9767 18.4701 32.6773 20.2835 32.0785 21.9942C31.4797 23.705 30.6329 25.2617 29.538 26.6646L38.1279 35.2548L35.2538 38.1289Z"
          fill="currentColor"
        />
      </svg>
    ),
    numberKey: "gazerResultCount",
    text: "checks done",
    variant: "success",
  },
  {
    icon: (
      <svg className="size-2_5rem text-primary" viewBox="0 0 35 36" xmlns="http://www.w3.org/2000/svg">
        <path
          d="M17.2025 25.7279C19.3379 25.7279 21.153 24.9805 22.6478 23.4857C24.1426 21.9909 24.89 20.1758 24.89 18.0404C24.89 15.9049 24.1426 14.0898 22.6478 12.5951C21.153 11.1003 19.3379 10.3529 17.2025 10.3529C15.0671 10.3529 13.252 11.1003 11.7572 12.5951C10.2624 14.0898 9.51497 15.9049 9.51497 18.0404C9.51497 20.1758 10.2624 21.9909 11.7572 23.4857C13.252 24.9805 15.0671 25.7279 17.2025 25.7279ZM17.2025 19.7487C16.7184 19.7487 16.3127 19.585 15.9853 19.2576C15.6579 18.9301 15.4941 18.5244 15.4941 18.0404C15.4941 17.5563 15.6579 17.1506 15.9853 16.8232C16.3127 16.4957 16.7184 16.332 17.2025 16.332C17.6865 16.332 18.0922 16.4957 18.4197 16.8232C18.7471 17.1506 18.9108 17.5563 18.9108 18.0404C18.9108 18.5244 18.7471 18.9301 18.4197 19.2576C18.0922 19.585 17.6865 19.7487 17.2025 19.7487ZM17.2025 35.1237C14.8393 35.1237 12.6184 34.6753 10.54 33.7784C8.4615 32.8815 6.65352 31.6643 5.11602 30.1268C3.57852 28.5893 2.36133 26.7813 1.46445 24.7029C0.567578 22.6244 0.119141 20.4036 0.119141 18.0404C0.119141 15.6772 0.567578 13.4563 1.46445 11.3779C2.36133 9.29939 3.57852 7.49141 5.11602 5.95391C6.65352 4.41641 8.4615 3.19922 10.54 2.30234C12.6184 1.40547 14.8393 0.957031 17.2025 0.957031C20.6191 0.957031 23.7368 1.8895 26.5556 3.75443C29.3743 5.61936 31.4671 8.10356 32.8337 11.207H28.99C27.7941 9.07161 26.1428 7.39887 24.0358 6.1888C21.9289 4.97873 19.6511 4.3737 17.2025 4.3737C13.3872 4.3737 10.1556 5.69766 7.50768 8.34557C4.85977 10.9935 3.53581 14.2251 3.53581 18.0404C3.53581 21.8556 4.85977 25.0872 7.50768 27.7352C10.1556 30.3831 13.3872 31.707 17.2025 31.707C19.1671 31.707 21.0462 31.3013 22.84 30.4898C24.6337 29.6784 26.1712 28.5181 27.4525 27.0091V31.707C25.9434 32.789 24.3205 33.6289 22.5837 34.2268C20.8469 34.8247 19.0532 35.1237 17.2025 35.1237ZM30.8691 28.2904V14.6237H34.2858V28.2904H30.8691ZM32.5775 35.1237C32.0934 35.1237 31.6877 34.96 31.3603 34.6325C31.0329 34.3051 30.8691 33.8994 30.8691 33.4154C30.8691 32.9313 31.0329 32.5256 31.3603 32.1982C31.6877 31.8707 32.0934 31.707 32.5775 31.707C33.0615 31.707 33.4672 31.8707 33.7947 32.1982C34.1221 32.5256 34.2858 32.9313 34.2858 33.4154C34.2858 33.8994 34.1221 34.3051 33.7947 34.6325C33.4672 34.96 33.0615 35.1237 32.5775 35.1237Z"
          fill="currentColor"
        />
      </svg>
    ),
    numberKey: "gazerDowntimeCount",
    text: "incidents caught",
    variant: "primary",
  },
  {
    icon: (
      <svg className="size-2_5rem text-info" viewBox="0 0 31 34" width="31" xmlns="http://www.w3.org/2000/svg">
        <path
          d="M25.902 9.14189C24.6323 9.14189 23.5531 8.69749 22.6643 7.8087C21.7755 6.9199 21.3311 5.84065 21.3311 4.57095C21.3311 3.30124 21.7755 2.22199 22.6643 1.33319C23.5531 0.444397 24.6323 0 25.902 0C27.1717 0 28.251 0.444397 29.1398 1.33319C30.0286 2.22199 30.473 3.30124 30.473 4.57095C30.473 5.84065 30.0286 6.9199 29.1398 7.8087C28.251 8.69749 27.1717 9.14189 25.902 9.14189ZM0 33.5203V6.09459C0 5.25659 0.298381 4.5392 0.895144 3.94244C1.49191 3.34568 2.20929 3.0473 3.0473 3.0473H18.4361C18.3346 3.55518 18.2838 4.06306 18.2838 4.57095C18.2838 5.07883 18.3346 5.58671 18.4361 6.09459C18.7917 7.87219 19.6678 9.33235 21.0644 10.4751C22.4611 11.6178 24.0736 12.1892 25.902 12.1892C26.7146 12.1892 27.5146 12.0622 28.3018 11.8083C29.089 11.5543 29.8127 11.1734 30.473 10.6655V24.3784C30.473 25.2164 30.1746 25.9338 29.5778 26.5305C28.9811 27.1273 28.2637 27.4257 27.4257 27.4257H6.09459L0 33.5203Z"
          fill="currentColor"
        />
      </svg>
    ),
    numberKey: "notificationCount",
    text: "notifications sent",
    variant: "info",
  },
];

export default function Statistics({ ...props }: StatisticsProps) {
  const { data: dailyStatistics } = useDailyStatistics({ refreshInterval: 10000 });

  return (
    <section {...props}>
      <div className="bg-body-tertiary pb-88 pt-24">
        <div className="container">
          <div className="mx-auto mw-32rem text-center">
            <SectionTag>RELIABLE METRICS</SectionTag>

            <h2 className="fs-3 fw-semi-bold mt-3">WebGazer runs like clockwork</h2>

            <div className="fs-5 mt-4 text-body-secondary">
              You can rest easy because WebGazer constantly monitors your business&apos; services, day and night.
            </div>
          </div>
        </div>
      </div>

      <div className="bg-body pb-24 pt-1">
        <div className="container mt-n72">
          <div className="fs-5 fw-mediumx  mb-8 mt-4 text-body-secondary text-center">
            Have a look at
            {" "}
            <span className="fw-medium text-decoration-underline">
              today
            </span>
            &apos;s statistics:
          </div>

          <div className="gx-xl-28 gy-12 gy-lg-0 row row-cols-1 row-cols-md-3">
            {stats.map((d) => (
              <div className="col" key={d.numberKey}>
                <div className="border-0 card overflow-hidden rounded-4 shadow-lg">
                  <div className="d-flex">
                    <div className={`bg-${d.variant} ps-5`} />

                    <div className="p-12">
                      {d.icon}

                      <div className="fs-2 fw-semi-bold mt-2">
                        {dailyStatistics !== undefined ? (
                          <CountUp enableScrollSpy end={dailyStatistics[d.numberKey]} preserveValue scrollSpyOnce />
                        ) : "..."}
                      </div>

                      <div className="fw-medium text-body-tertiary">{d.text}</div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
